import { Link, useNavigate } from "react-router-dom";
import { Music, LogOut, Flame, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useStats } from "@/context/StatsContext";

export default function Header() {
  const { user, logout } = useAuth();
  const stats = useStats()?.stats;
  const nav = useNavigate();

  const pct = stats && stats.xp_for_next_level
    ? Math.min(100, Math.round((stats.xp_into_level / stats.xp_for_next_level) * 100))
    : 0;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-30 bg-background/70 backdrop-blur-xl border-b border-border"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
        <Link to={user ? "/library" : "/"} data-testid="header-logo" className="flex items-center gap-2.5 group">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-primary/50 text-primary transition-shadow duration-300 group-hover:glow-amber-sm">
            <Music size={17} />
          </span>
          <span className="font-display italic text-2xl tracking-tight">Lyric Lock</span>
        </Link>

        {user && user.id ? (
          <div className="flex items-center gap-2 md:gap-5">
            {/* How it works */}
            <Link to="/how-it-works" className="hidden sm:inline-flex text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="header-how-link">
              How it works
            </Link>
            {/* Streak */}
            <div className="hidden sm:flex items-center gap-1.5 text-sm" data-testid="header-streak">
              <Flame size={16} className={stats?.streak_days > 0 ? "text-primary" : "text-muted-foreground"} />
              <span className="font-mono font-medium">{stats?.streak_days || 0}</span>
              <span className="overline-label ml-0.5">day</span>
            </div>
            {/* Level + XP bar */}
            <div className="hidden md:flex items-center gap-3" data-testid="header-level">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                <Trophy size={14} className="text-primary" /> <span className="font-mono">Lv&nbsp;{stats?.level || 1}</span>
              </span>
              <div className="w-28 h-1.5 rounded-full bg-secondary overflow-hidden" title={`${stats?.xp || 0} XP`}>
                <div className="h-full bg-primary transition-all duration-500 glow-amber-sm" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <Button
              data-testid="header-logout-btn"
              variant="ghost"
              size="sm"
              onClick={() => { logout(); nav("/"); }}
              className="rounded-full text-muted-foreground hover:text-foreground"
            >
              <LogOut size={16} className="mr-1.5 hidden sm:block" /> Sign out
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/how-it-works" className="hidden sm:inline-flex text-sm text-muted-foreground hover:text-foreground transition-colors mr-2" data-testid="header-how-link-public">
              How it works
            </Link>
            <Link to="/auth?mode=login">
              <Button data-testid="header-login-btn" variant="ghost" size="sm" className="rounded-full text-muted-foreground hover:text-foreground">Sign in</Button>
            </Link>
            <Link to="/auth?mode=register">
              <Button data-testid="header-register-btn" size="sm" className="rounded-full">Start free</Button>
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
